import type React from 'react'
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react'

export type StepId = string

interface StepProviderValue {
	activeStepId: StepId
	canNext: () => boolean
	goToNextStep: () => void
	goToPreviousStep: () => void
}

const StepContext = createContext<StepProviderValue | undefined>(undefined)

export const StepProvider = <T extends StepId[]>({
	initialStepId = 'locationType',
	onPrevious = () => true,
	canNext = () => true,
	getPreviousStepId,
	getNextStepId,
	renderStep,
}: {
	steps: T
	initialStepId?: T[number]
	onPrevious?: (props: { activeStepId: T[number] }) => boolean
	canNext?: (props: { activeStepId: T[number] }) => boolean
	getPreviousStepId: (props: { activeStepId: T[number] }) => T[number] | null
	getNextStepId: (props: { activeStepId: T[number] }) => T[number] | null
	renderStep: (props: { activeStepId: T[number] }) => React.ReactNode
}) => {
	const [activeStepId, setStepId] = useState<T[number]>(initialStepId)

	const goToPreviousStep = useCallback(() => {
		const prevStepId = getPreviousStepId({ activeStepId })

		if (prevStepId && onPrevious({ activeStepId })) {
			setStepId(prevStepId)
		}
	}, [activeStepId, getPreviousStepId, onPrevious])

	const goToNextStep = useCallback(() => {
		const nextStepId = getNextStepId({ activeStepId })

		if (nextStepId && canNext({ activeStepId })) {
			setStepId(nextStepId)
		}
	}, [activeStepId, getNextStepId, canNext])

	const value = useMemo(
		() => ({
			activeStepId,
			canNext: () => canNext({ activeStepId }),
			goToNextStep,
			goToPreviousStep,
		}),
		[activeStepId, goToNextStep, canNext, goToPreviousStep],
	)

	return (
		<StepContext.Provider value={value}>
			{renderStep({ activeStepId })}
		</StepContext.Provider>
	)
}

export const useStep = () => {
	const context = useContext(StepContext)

	if (context === undefined) {
		throw new Error('useStep must be used within a StepProvider')
	}

	return context
}
